'use client';
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import type { Event } from './mockData';
import { createClient } from '@/lib/supabase/client';
import { toast } from 'sonner';

interface EventContextType {
  activeEvent: Event | null;
  setActiveEvent: (event: Event) => void;
  events: Event[];
  setEvents: React.Dispatch<React.SetStateAction<Event[]>>;
  pendingEvent: Event | null;
  setPendingEvent: (event: Event | null) => void;
  confirmEventSwitch: () => void;
  cancelEventSwitch: () => void;
  addEvent: (event: Event) => Promise<void>;
  updateEvent: (event: Event) => Promise<void>;
  deleteEvent: (id: string) => Promise<void>;
  loading: boolean;
  error: string | null;
}

const EventContext = createContext<EventContextType | null>(null);

function rowToEvent(row: Record<string, unknown>): Event {
  return {
    id: row.id as string,
    name: row.name as string,
    client: row.client as string,
    date: row.date as string,
    location: row.location as string,
    riskLevel: row.risk_level as Event['riskLevel'],
    expectedVisitors: row.expected_visitors as number,
    status: row.status as Event['status'],
    invoiceStatus: row.invoice_status as Event['invoiceStatus'],
    gritMapUrl: (row.grit_map_url as string | null) ?? undefined,
    createdAt: row.created_at as string,
  };
}

function eventToRow(event: Event) {
  return {
    id: event.id,
    name: event.name,
    client: event.client,
    date: event.date,
    location: event.location,
    risk_level: event.riskLevel,
    expected_visitors: event.expectedVisitors,
    status: event.status,
    invoice_status: event.invoiceStatus,
    grit_map_url: event.gritMapUrl ?? null,
  };
}

export function EventProvider({ children }: { children: React.ReactNode }) {
  const [events, setEventsState] = useState<Event[]>([]);
  const [activeEvent, setActiveEventState] = useState<Event | null>(null);
  const [pendingEvent, setPendingEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const activeEventIdRef = useRef<string | null>(null);

  // Load events from Supabase on mount — NO mock fallback
  useEffect(() => {
    let cancelled = false;
    const loadFromSupabase = async () => {
      setLoading(true);
      setError(null);
      try {
        const supabase = createClient();
        const { data, error: fetchError } = await supabase
          .from('events')
          .select('*')
          .order('created_at', { ascending: false });

        if (cancelled) return;

        if (fetchError) {
          setError(fetchError.message);
          setLoading(false);
          return;
        }

        const loaded = (data ?? []).map(rowToEvent);
        setEventsState(loaded);

        // Set active event: keep current if still exists, else first
        if (loaded.length > 0) {
          const currentId = activeEventIdRef.current;
          const found = currentId ? loaded.find(e => e.id === currentId) : null;
          setActiveEventState(found ?? loaded[0]);
          activeEventIdRef.current = (found ?? loaded[0]).id;
        } else {
          setActiveEventState(null);
          activeEventIdRef.current = null;
        }
      } catch (err: unknown) {
        if (!cancelled) {
          const msg = err instanceof Error ? err.message : 'Onbekende fout';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadFromSupabase();
    return () => { cancelled = true; };
  }, []);

  // Real-time subscription — always active, no hydration gate
  useEffect(() => {
    const supabase = createClient();
    const channel = supabase
      .channel('events_realtime_v2')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'events' },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const ev = rowToEvent(payload.new as Record<string, unknown>);
            setEventsState(prev => {
              if (prev.find(e => e.id === ev.id)) return prev;
              return [ev, ...prev];
            });
            // If no active event yet, set this as active
            setActiveEventState(prev => {
              if (!prev) {
                activeEventIdRef.current = ev.id;
                return ev;
              }
              return prev;
            });
          } else if (payload.eventType === 'UPDATE') {
            const ev = rowToEvent(payload.new as Record<string, unknown>);
            setEventsState(prev => prev.map(e => e.id === ev.id ? ev : e));
            // Update active event if it was updated
            setActiveEventState(prev => {
              if (prev && prev.id === ev.id) return ev;
              return prev;
            });
          } else if (payload.eventType === 'DELETE') {
            const deletedId = (payload.old as Record<string, unknown>).id as string;
            setEventsState(prev => prev.filter(e => e.id !== deletedId));
            // If active event was deleted, switch to first remaining
            setActiveEventState(prev => {
              if (prev && prev.id === deletedId) {
                setEventsState(current => {
                  const remaining = current.filter(e => e.id !== deletedId);
                  const next = remaining[0] ?? null;
                  activeEventIdRef.current = next?.id ?? null;
                  // We can't set state here directly, schedule it
                  setTimeout(() => setActiveEventState(next), 0);
                  return remaining;
                });
                return prev; // will be replaced by setTimeout above
              }
              return prev;
            });
          }
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  const setEvents: React.Dispatch<React.SetStateAction<Event[]>> = useCallback((action) => {
    setEventsState(action);
  }, []);

  const addEvent = useCallback(async (event: Event) => {
    // Optimistic insert
    setEventsState(prev => [event, ...prev.filter(e => e.id !== event.id)]);
    if (!activeEvent) {
      setActiveEventState(event);
      activeEventIdRef.current = event.id;
    }

    const supabase = createClient();
    const { error: insertError } = await supabase.from('events').insert(eventToRow(event));

    if (insertError) {
      // Revert
      setEventsState(prev => prev.filter(e => e.id !== event.id));
      if (activeEventIdRef.current === event.id) {
        setActiveEventState(null);
        activeEventIdRef.current = null;
      }
      toast.error(`Fout bij aanmaken event: ${insertError.message}`);
      throw insertError;
    }

    // Seed default units for the new event (trigger handles it server-side,
    // but we also call the RPC directly as a reliable fallback)
    try {
      await supabase.rpc('seed_default_units_for_event', { p_event_id: event.id });
    } catch {
      // Non-fatal: trigger already handles this, ignore client-side RPC errors
    }
  }, [activeEvent]);

  const updateEvent = useCallback(async (event: Event) => {
    // Save snapshot for rollback
    let snapshot: Event[] = [];
    let activeSnapshot: Event | null = null;

    setEventsState(prev => {
      snapshot = prev;
      return prev.map(e => e.id === event.id ? event : e);
    });
    setActiveEventState(prev => {
      activeSnapshot = prev;
      if (prev && prev.id === event.id) return event;
      return prev;
    });

    const supabase = createClient();
    const { error: updateError } = await supabase
      .from('events')
      .update(eventToRow(event))
      .eq('id', event.id);

    if (updateError) {
      // Revert
      setEventsState(snapshot);
      setActiveEventState(activeSnapshot);
      toast.error(`Fout bij opslaan event: ${updateError.message}`);
      throw updateError;
    }
  }, []);

  const deleteEvent = useCallback(async (id: string) => {
    // Save snapshot for rollback
    let snapshot: Event[] = [];

    setEventsState(prev => {
      snapshot = prev;
      return prev.filter(e => e.id !== id);
    });

    // If deleting active event, switch to first remaining
    setActiveEventState(prev => {
      if (prev && prev.id === id) {
        const remaining = snapshot.filter(e => e.id !== id);
        const next = remaining[0] ?? null;
        activeEventIdRef.current = next?.id ?? null;
        return next;
      }
      return prev;
    });

    const supabase = createClient();
    const { error: deleteError } = await supabase.from('events').delete().eq('id', id);

    if (deleteError) {
      // Revert
      setEventsState(snapshot);
      setActiveEventState(prev => {
        if (!prev) {
          const original = snapshot.find(e => e.id === id) ?? null;
          activeEventIdRef.current = original?.id ?? null;
          return original;
        }
        return prev;
      });
      toast.error(`Fout bij verwijderen event: ${deleteError.message}`);
      throw deleteError;
    }
  }, []);

  const setActiveEvent = useCallback((event: Event) => {
    if (event.id !== activeEventIdRef.current) {
      setPendingEvent(event);
    }
  }, []);

  const confirmEventSwitch = useCallback(() => {
    if (pendingEvent) {
      setActiveEventState(pendingEvent);
      activeEventIdRef.current = pendingEvent.id;
      setPendingEvent(null);
    }
  }, [pendingEvent]);

  const cancelEventSwitch = useCallback(() => {
    setPendingEvent(null);
  }, []);

  return (
    <EventContext.Provider value={{
      activeEvent,
      setActiveEvent,
      events,
      setEvents,
      pendingEvent,
      setPendingEvent,
      confirmEventSwitch,
      cancelEventSwitch,
      addEvent,
      updateEvent,
      deleteEvent,
      loading,
      error,
    }}>
      {children}
    </EventContext.Provider>
  );
}

export function useEvent() {
  const ctx = useContext(EventContext);
  if (!ctx) throw new Error('useEvent must be used within EventProvider');
  return ctx;
}